/**
 * Colour theme preference.
 *
 * A presentation preference only: it is stored in the browser, never sent to the
 * API, and grants nothing. The token values for each theme live in
 * `packages/ui/src/styles/tokens.css`; this module only selects one by setting
 * `data-theme` on <html>.
 *
 * `public/theme-init.js` applies the stored theme before first paint so a dark-theme
 * user never sees a light flash. It duplicates the storage key and the attribute
 * below on purpose — it runs before any bundle loads — so keep the two in step.
 */

export const THEMES = ['light', 'dark'] as const;

export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = 'light';

/** Keep in step with `public/theme-init.js`. */
export const THEME_STORAGE_KEY = 'prisco.theme';

export const THEME_LABEL: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
};

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/** The saved theme, or the default when nothing valid is saved or storage is blocked. */
export function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function storeTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage blocked (private mode, policy): the choice lasts for this page only.
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}
